import { UserCoinsAmount } from "@/widgets/user-coins-amount";
import { UserMenu } from "@/widgets/user-menu";
import { NavLink, useLocation } from "react-router";
import LogoNav from "@/shared/icons/logo_nav.svg?react";
import LogoCircle from "@/shared/icons/logo_circle.svg?react";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { resetStates } from "@/shared/lib/utils";
import { ChangeLang } from "@/features/change-lang/ui/ChangeLang";
import { NewsBadge } from "@/features/news-badge";
import CloudsHands from "@/shared/components/clouads-hands/CloudsHands";
import {
  getLandingCardsCatalogUrl,
  getLandingSpreadsCatalogUrl,
} from "@/shared/config/marketingUrls";
import { normalizeAppLocale } from "@/shared/lib/appLocale";
import { HeaderAnimatedOutlet } from "./HeaderAnimatedOutlet";

const navTextLinkClass =
  "hidden md:block font-normal opacity-80 hover:opacity-100 transition-opacity duration-200";

export const Header = memo(() => {
  const { isLoading, user } = useKindeAuth();
  const [hasSeenUser, setHasSeenUser] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation("layout");
  const showCloudsHands = location.pathname === "/ask";
  const appLocale = normalizeAppLocale(i18n.resolvedLanguage ?? i18n.language);
  const spreadsCatalogUrl = getLandingSpreadsCatalogUrl(appLocale);
  const cardsCatalogUrl = getLandingCardsCatalogUrl(appLocale);

  useEffect(() => {
    if (user) {
      setHasSeenUser(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    // Генерируем уникальный ключ для текущего пользователя
    const storageKey = `last_seen_update_${user.id}`;

    // 1. Проверяем, когда обновляли в последний раз
    const lastUpdate = localStorage.getItem(storageKey);
    const now = Date.now();
    const UPDATE_INTERVAL = 15 * 60 * 1000; //15 mins

    // Проверяем таймер именно для ЭТОГО пользователя
    if (lastUpdate && now - parseInt(lastUpdate) < UPDATE_INTERVAL) {
      return;
    }

    // 2. Отправляем запрос
    fetch("/api/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: user.id,
        email: user.email,
        givenName: user.givenName,
        familyName: user.familyName,
      }),
    })
      .then((res) => {
        // 3. Сохраняем время обновления ТОЛЬКО при успехе
        if (res.ok) {
          // Сохраняем время обновления в уникальный ключ
          localStorage.setItem(storageKey, now.toString());
        }
      })
      .catch(console.error);
  }, [user]);

  if (isLoading && !hasSeenUser && !user) {
    return <div>{t("common.loading")}</div>;
  }

  // if (!isAuthenticated) {
  //   // можно сделать redirect
  //   if (typeof window !== "undefined") {
  //     window.location.href = "/";
  //   }
  //   return null;
  // }

  return (
    <div>
      <header className="flex justify-between rounded-[100px] max-w-[1440px] mx-auto relative px-3 py-2.5 md:p-5 overflow-hidden z-100">
        <nav className="z-3 flex gap-8 items-center text-white text-[16px] font-body">
          <NavLink to="/" onClick={resetStates}>
            <LogoCircle className="block min-[500px]:hidden h-7 w-auto" />
            <LogoNav className="hidden min-[500px]:block w-[157px] h-8" />
          </NavLink>
          <NavLink
            to="/ask"
            onClick={resetStates}
            className={navTextLinkClass}
          >
            {t("navigation.tarotReadings")}
          </NavLink>

          <a href={spreadsCatalogUrl} className={navTextLinkClass}>
            {t("navigation.spreadsCatalog")}
          </a>

          <a href={cardsCatalogUrl} className={navTextLinkClass}>
            {t("navigation.cardsCatalog")}
          </a>

          <NavLink
            to="/daily"
            onClick={resetStates}
            className={navTextLinkClass}
          >
            {t("navigation.cardOfTheDay")}
          </NavLink>

          <NavLink
            to="/pricing"
            onClick={resetStates}
            className={navTextLinkClass}
          >
            {t("navigation.pricing")}
          </NavLink>
        </nav>
        <div className="flex gap-3">
          <ChangeLang />
          <NewsBadge />
          <UserCoinsAmount />
          <UserMenu />
        </div>
      </header>
      {showCloudsHands && <CloudsHands />}
      <main className="relative">
        <HeaderAnimatedOutlet />
      </main>
    </div>
  );
});
